import React, { useLayoutEffect } from "react";
import "./SettingsPage.css";

import { useAuth } from "../../hooks/useAuth";

import FiltersIcon from "../../assets/Icons/FiltersIcon.svg?react";

import Text from "../../components/Text/Text";
import Flex from "../../components/Flex/Flex";

import Button from "../../components/Button/Button";
import TableSizeFilter from "../../components/TableSizeFilter/TableSizeFilter";
import LimitFilter from "../../components/LimitFilter/LimitsFilter";
import DateFilter, {
  CustomDateFilterValue,
} from "../../components/DateFilter/DateFilter";
import {
  getPresetFiltersLocalStorage,
  setPresetFiltersLocalStorage,
} from "../../localStorage/presetFiltersLocalStorage";
import RandomizerForm from "../../components/RandomizerForm/RandomizerForm";
import { useTranslation } from "react-i18next";
import Checkbox, { CheckboxChangeEvent } from "rc-checkbox";

const SettingsPage = React.memo(() => {
  const { t } = useTranslation("translation");
  useAuth();

  const [tableSizeFilterValue, setTableSizeFilterValue] = React.useState<
    number | undefined
  >();

  const [limitFilterValue, setLimitFilterValue] = React.useState<number>(0);

  const [dateFilterValue, setDateFilterValue] = React.useState<
    number | undefined
  >();

  const [customDateFilterValue, setCustomDateFilterValue] = React.useState<
    CustomDateFilterValue | undefined
  >();

  const [smartSearch, setSmartSearch] = React.useState<boolean>(
    localStorage.getItem("smart-search") === "true"
  );

  const handleChangeSmartSearch = React.useCallback(
    (e: CheckboxChangeEvent) => {
      setSmartSearch(e.target.checked);
      localStorage.setItem("smart-search", String(e.target.checked));
    },
    []
  );

  useLayoutEffect(() => {
    const {
      tableSizeFilterValue,
      limitFilterValue,
      dateFilterValue,
      customDateFilterValue,
    } = getPresetFiltersLocalStorage();

    setTableSizeFilterValue(tableSizeFilterValue);
    setLimitFilterValue(limitFilterValue);
    setDateFilterValue(dateFilterValue);
    setCustomDateFilterValue(customDateFilterValue);
  }, []);

  const handleChangeTableSizeFilter = React.useCallback((value?: number) => {
    setTableSizeFilterValue(value);
  }, []);

  const handleChangeLimitFilter = React.useCallback((value: number) => {
    setLimitFilterValue(value);
  }, []);

  const handleDateFilter = React.useCallback(
    (value?: number, customValue?: CustomDateFilterValue) => {
      setDateFilterValue(value);
      setCustomDateFilterValue(customValue);
    },
    []
  );

  const handleApply = React.useCallback(() => {
    setPresetFiltersLocalStorage(
      tableSizeFilterValue,
      limitFilterValue,
      dateFilterValue,
      customDateFilterValue
    );
  }, [
    tableSizeFilterValue,
    limitFilterValue,
    dateFilterValue,
    customDateFilterValue,
  ]);

  const handleReset = React.useCallback(() => {
    setTableSizeFilterValue(undefined);
    setLimitFilterValue(0);
    setDateFilterValue(undefined);
    setCustomDateFilterValue(undefined);
  }, []);

  return (
    <Flex
      vertical
      className="SettingsPage"
    >
      <Text
        size={6}
        bold
      >
        {t("settings")}
      </Text>
      <div className="SettingsPage__Content">
        <Flex
          vertical
          gap={32}
        >
          <Flex align="center">
            <i className="SettingsPage__FiltersIcon">
              <FiltersIcon />
            </i>
            <Text
              size={4}
              bold
            >
              {t("preset_filters")}
            </Text>
          </Flex>
          <Flex
            gap={32}
            wrap
          >
            <TableSizeFilter
              value={tableSizeFilterValue}
              onChange={handleChangeTableSizeFilter}
            />

            <LimitFilter
              value={limitFilterValue}
              onChange={handleChangeLimitFilter}
            />
            <DateFilter
              value={dateFilterValue}
              customValue={customDateFilterValue}
              onChange={handleDateFilter}
            />
          </Flex>

          <Flex
            justify="flex-end"
            gap={16}
          >
            <Button onClick={handleReset}>{t("reset")}</Button>
            <Button
              type="primary"
              onClick={handleApply}
            >
              {t("apply")}
            </Button>
          </Flex>
        </Flex>
      </div>
      <RandomizerForm />
      <div className="SettingsPage__Content">
        <Flex
          vertical
          gap={16}
        >
          <label>
            <Checkbox
              checked={smartSearch}
              onChange={handleChangeSmartSearch}
            />
            &nbsp;
            <Text
              size={3}
              bold
            >
              {t("smart_search")}
            </Text>
          </label>
        </Flex>
      </div>
    </Flex>
  );
});

export default SettingsPage;
